import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../components/SEO';

const SNAPSHOT_URL = '/status/latest.json';
const SNAPSHOT_MAX_AGE_MS = 5 * 60 * 1000;

const STATUS_COPY = {
  operational: {
    heading: 'All systems operational',
    label: 'Operational',
    tone: 'operational',
  },
  degraded_performance: {
    heading: 'Degraded performance',
    label: 'Degraded',
    tone: 'degraded',
  },
  partial_outage: {
    heading: 'Partial outage',
    label: 'Partial outage',
    tone: 'outage',
  },
  major_outage: {
    heading: 'Major outage',
    label: 'Major outage',
    tone: 'outage',
  },
  maintenance: {
    heading: 'Maintenance in progress',
    label: 'Maintenance',
    tone: 'maintenance',
  },
  unknown: {
    heading: 'Unable to confirm live system status',
    label: 'Unknown',
    tone: 'unknown',
  },
};

const DEFAULT_COMPONENTS = [
  { id: 'web-app', name: 'Web app', status: 'unknown' },
  { id: 'api', name: 'API', status: 'unknown' },
  { id: 'authentication', name: 'Authentication', status: 'unknown' },
  { id: 'workflow-automation', name: 'Workflow automation', status: 'unknown' },
  { id: 'data-services', name: 'Data services', status: 'unknown' },
];

function resolveLiveStatusUrl() {
  if (process.env.REACT_APP_STATUS_API_URL) {
    return process.env.REACT_APP_STATUS_API_URL;
  }

  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))
  ) {
    return 'http://admin.localhost:8000/api/status/';
  }

  return 'https://admin.phennx.io/api/status/';
}

async function fetchStatusJson(url) {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Status request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (!payload || typeof payload !== 'object' || !STATUS_COPY[payload.status]) {
    throw new Error('Status payload is invalid');
  }

  return {
    status: payload.status,
    generated_at: payload.generated_at,
    source: payload.source || 'snapshot',
    components: Array.isArray(payload.components) ? payload.components : [],
    notices: Array.isArray(payload.notices) ? payload.notices : [],
  };
}

function isFreshSnapshot(payload) {
  const generatedAt = Date.parse(payload?.generated_at || '');
  if (!Number.isFinite(generatedAt)) {
    return false;
  }
  return Date.now() - generatedAt <= SNAPSHOT_MAX_AGE_MS;
}

function buildUnknownStatus(snapshot, detail) {
  const generatedAt = snapshot?.generated_at || new Date(Date.now()).toISOString();
  return {
    status: 'unknown',
    generated_at: generatedAt,
    source: 'snapshot',
    components: DEFAULT_COMPONENTS.map((component) => ({
      ...component,
      updated_at: generatedAt,
    })),
    notices: snapshot?.notices || [],
    detail,
    isStale: Boolean(snapshot),
  };
}

function formatDateTime(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function getStatusCopy(status) {
  return STATUS_COPY[status] || STATUS_COPY.unknown;
}

function StatusBadge({ status }) {
  const copy = getStatusCopy(status);
  return <span className={`status-badge status-badge--${copy.tone}`}>{copy.label}</span>;
}

function SourceLine({ data }) {
  if (!data) {
    return 'Checking live status';
  }
  if (data.status === 'unknown' && data.isStale) {
    return 'Snapshot is stale';
  }
  if (data.source === 'live') {
    return 'Live status from Phennx operations';
  }
  return 'Snapshot fallback from CDN';
}

export default function StatusPage() {
  const [state, setState] = useState({
    loading: true,
    data: null,
  });

  useEffect(() => {
    let isMounted = true;

    const loadStatus = async () => {
      try {
        const livePayload = await fetchStatusJson(resolveLiveStatusUrl());
        if (isMounted) {
          setState({ loading: false, data: { ...livePayload, source: 'live' } });
        }
        return;
      } catch (liveError) {
        try {
          const snapshotPayload = await fetchStatusJson(SNAPSHOT_URL);
          if (!isFreshSnapshot(snapshotPayload)) {
            if (isMounted) {
              setState({
                loading: false,
                data: buildUnknownStatus(snapshotPayload, 'Unable to confirm live system status.'),
              });
            }
            return;
          }
          if (isMounted) {
            setState({ loading: false, data: { ...snapshotPayload, source: 'snapshot' } });
          }
        } catch (snapshotError) {
          if (isMounted) {
            setState({
              loading: false,
              data: buildUnknownStatus(null, 'Unable to load live or snapshot status data.'),
            });
          }
        }
      }
    };

    loadStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  const data = state.data;
  const statusCopy = getStatusCopy(data?.status || 'unknown');
  const components = data?.components?.length ? data.components : DEFAULT_COMPONENTS;
  const notices = data?.notices || [];
  const schema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://phennx.io/status#webpage',
      url: 'https://phennx.io/status',
      name: 'Phennx Status',
      description: 'Current availability and maintenance status for the Phennx platform.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
    }),
    []
  );

  return (
    <>
      <SEO
        title="Phennx Status"
        description="Current availability and maintenance status for the Phennx platform."
        path="/status"
        image="/logo-full-transparent.png"
        schema={schema}
      />
      <article className="status-page" aria-label="Phennx system status">
        <header className={`status-page__hero status-page__hero--${statusCopy.tone}`}>
          <div className="section-inner">
            <p className="status-page__eyebrow">System status</p>
            <h1>{state.loading ? 'Checking system status' : statusCopy.heading}</h1>
            <p className="status-page__lede">
              {state.loading
                ? 'Confirming the current Phennx platform status.'
                : data?.detail || 'Current availability across the public Phennx platform.'}
            </p>
            <div className="status-page__meta">
              <StatusBadge status={data?.status || 'unknown'} />
              <span>{SourceLine({ data })}</span>
              <span>Updated {data?.generated_at ? formatDateTime(data.generated_at) : 'pending'}</span>
            </div>
          </div>
        </header>

        <section className="status-page__content">
          <div className="section-inner">
            <div className="status-page__grid" aria-label="Component status">
              {components.map((component) => (
                <article className="status-component" key={component.id}>
                  <div>
                    <h2>{component.name}</h2>
                    <p>Updated {component.updated_at ? formatDateTime(component.updated_at) : 'pending'}</p>
                  </div>
                  <StatusBadge status={component.status} />
                </article>
              ))}
            </div>

            <section className="status-notices" aria-labelledby="status-notices-title">
              <div className="status-notices__header">
                <p className="status-page__eyebrow">Notices</p>
                <h2 id="status-notices-title">Current incidents and maintenance</h2>
              </div>
              {notices.length ? (
                <div className="status-notices__list">
                  {notices.map((notice) => (
                    <article className="status-notice" key={notice.id}>
                      <div>
                        <p className="status-notice__type">{notice.type}</p>
                        <h3>{notice.title}</h3>
                        <p>
                          {notice.status.replace(/_/g, ' ')} · Updated {formatDateTime(notice.updated_at)}
                        </p>
                      </div>
                      <span className="status-notice__impact">{notice.impact}</span>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="status-notice status-notice--empty">
                  <h3>No active incidents or maintenance</h3>
                  <p>No public notices are currently reported.</p>
                </div>
              )}
            </section>
          </div>
        </section>
      </article>
    </>
  );
}
